/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { deepCloneObject } from '../../utils/object';
import { getId } from '../../utils/item';
import { ConditionSettingsDisplayField } from './condition.SettingsGetField';
import { TextareaAutosize } from '@mui/material';
import { BtnSimple } from '../form/btn.Simple';
import { IconPicker } from '../icons/icon.Picker';
import { SettingsInput } from '../form/settings.Widget';

export const ConditionSettingsEditState = props => {
  const { condition, state, itemId, updateField } = props;

  const item = useSelector(state => state.items[itemId]);
  const [itemNums, setItemNums] = useState([]);
  const [itemWidgets, setItemWidgets] = useState([]);
  const [expanded, setExpanded] = useState({
    item: false,
    widgets: false,
  });
  const [formula, setFormula] = useState('');

  const updateInState = (value, field, fieldId, subField) => {
    const clonedCondition = deepCloneObject(condition);
    if (fieldId) {
      clonedCondition[state][field][fieldId][subField] = value;
    } else {
      clonedCondition[state][field] = value;
    }
    updateField(clonedCondition[state], state);
  };

  const addImpactedField = (fieldObj, type, itemId) => {
    const clonedCondition = deepCloneObject(condition);
    const fieldId = getId(fieldObj);
    let clonedFields = deepCloneObject(clonedCondition?.[state]?.fields || {});
    clonedFields = {
      ...clonedFields,
      [fieldId]: { type, symbole: '=', value: 0, itemId, target: 'value' },
    };

    updateInState(clonedFields, 'fields');
  };

  const buildWidgetList = currentItem => {
    const widgets = [];
    const addWidgets = (collection, type, getLabel) => {
      if (!Array.isArray(collection)) return;
      collection.forEach(entry => {
        const entryId = getId(entry);
        const data = entry?.[entryId];
        if (!entryId || !data) {
          return;
        }
        const label =
          typeof getLabel === 'function'
            ? getLabel(data)
            : data?.label || type;
        widgets.push({
          id: entryId,
          key: `${type}:${entryId}`,
          type,
          label: label || type,
        });
      });
    };
    addWidgets(currentItem?.has_num, 'has_num', num => num?.label || 'Nombre');
    addWidgets(currentItem?.text, 'text', text =>
      text?.label
        ? text.label
        : text?.text
        ? `${text.text.slice(0, 32)}${text.text.length > 32 ? '…' : ''}`
        : 'Texte'
    );
    addWidgets(currentItem?.pics, 'pics', pic => pic?.label || 'Image');
    addWidgets(currentItem?.conditions, 'conditions', con =>
      con?.active?.label || 'Condition'
    );
    addWidgets(currentItem?.filters, 'filters', filter => filter?.label);
    addWidgets(currentItem?.iframes, 'iframes', iframe => iframe?.label || 'Iframe');
    addWidgets(currentItem?.timelines, 'timeline', timeline => timeline?.label || 'Timeline');
    addWidgets(currentItem?.links, 'link', link => link?.label || 'Lien');
    addWidgets(currentItem?.dates, 'dates', date => date?.label || 'Date');
    return widgets;
  };

  useEffect(() => {
    if (item?.has_num) {
      const arr = item?.has_num.map(numObj => numObj[Object.keys(numObj)]);
      setItemNums(arr);
    } else {
      setItemNums([]);
    }
    setItemWidgets(buildWidgetList(item));
  }, [item]);

  useEffect(() => {
    if (condition?.[state]?.condition) {
      setFormula(condition?.[state]?.condition);
    }
  }, [condition?.[state]?.condition]);

  const selectedIconName = condition?.[state]?.icon;

  const widgetsVisibility = useMemo(() => {
    return condition?.[state]?.widgetsVisibility || {};
  }, [condition, state]);

  const updateWidgetVisibility = (widgetKey, shouldHide) => {
    const nextVisibility = { ...widgetsVisibility };
    if (shouldHide) {
      nextVisibility[widgetKey] = 'hidden';
    } else {
      delete nextVisibility[widgetKey];
    }
    updateInState(nextVisibility, 'widgetsVisibility');
  };

  return (
    <div className='text-contrast'>
      <div className='flex flex-1 items-center gap-05'>
        <div className='ml-1'>Label</div>
        <SettingsInput
          placeholder={'label'}
          initValue={condition?.[state]?.label}
          onBlur={value => updateInState(value, 'label')}
        />
      </div>
      <IconPicker
        size={20}
        value={selectedIconName}
        onChange={label => updateInState(label, 'icon')}
        placeholder='recherche icône'
        label='Icône'
        previewSize={24}
        inputClassName='w-full'
        maxHeight={120}
        className='flex flex-1 items-center gap-05 flex-wrap mt-1'
      />
      <div>
        <TextareaAutosize
          aria-label='empty textarea'
          minRows={3}
          style={{ width: '100%' }}
          value={formula}
          className='w-full p-05 mt-1 bg-main bg-opacity-50 rounded'
          placeholder={'condition'}
          onChange={e => setFormula(e.target.value)}
          onBlur={() => updateInState(formula, 'condition')}
        />
      </div>

      <div>
        {Object.keys(condition?.[state]?.fields || []).map(fieldId => (
          <ConditionSettingsDisplayField
            key={fieldId}
            itemId={itemId}
            fieldId={fieldId}
            updateInState={updateInState}
            condition={condition}
            state={state}
          />
        ))}
      </div>

      <Accordion
        expanded={expanded.item}
        onChange={() =>
          setExpanded(prev => {
            return { ...prev, item: !prev.item };
          })
        }
      >
        <AccordionSummary
          aria-controls='item-content'
          id='item-header'
        >
          <div className='w-full flex items-center text-contrast'>
            <div className='flex items-center gap-05 flex-1 font-condensed text-lg mb-05 mt-1'>
              {expanded.item ? <FaChevronUp /> : <FaChevronDown />} Nombres de
              l'item
            </div>
          </div>
        </AccordionSummary>
        <AccordionDetails>
          <div className='w-full flex flex-wrap gap-1 text-contrast'>
            {itemNums?.map((numObj, i) => {
              return (
                <div
                  key={i}
                  className={`bg-primary px-05 py-01 shadow rounded text-sm `}
                  onClick={() =>
                    addImpactedField(item.has_num[i], 'has_num', itemId)
                  }
                >
                  <span className='text-opacity-50 text-sm uppercase text-contrast text-xs'>
                    {item.label} -
                  </span>{' '}
                  {numObj.label}
                </div>
              );
            })}
          </div>
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expanded.widgets}
        onChange={() =>
          setExpanded(prev => {
            return { ...prev, widgets: !prev.widgets };
          })
        }
        className='mt-1'
      >
        <AccordionSummary
          aria-controls='widgets-content'
          id='widgets-header'
        >
          <div className='w-full flex items-center text-contrast'>
            <div className='flex items-center gap-05 flex-1 font-condensed text-lg mb-05 mt-1'>
              {expanded.widgets ? <FaChevronUp /> : <FaChevronDown />}{' '}
              Widgets
            </div>
          </div>
        </AccordionSummary>
        <AccordionDetails>
          {itemWidgets.length === 0 ? (
            <div className='text-sm text-contrast text-opacity-70'>
              Aucun widget n&apos;est disponible sur cet item.
            </div>
          ) : (
            <div className='flex flex-col gap-05 w-full'>
              {itemWidgets.map(widget => {
                const isHidden =
                  widgetsVisibility?.[widget.key] === 'hidden' ||
                  widgetsVisibility?.[widget.id] === 'hidden';
                return (
                  <div
                    key={widget.key}
                    className='flex flex-wrap items-center justify-between gap-05 border border-main border-opacity-40 rounded px-05 py-03'
                  >
                    <div className='text-xs uppercase text-contrast text-opacity-70'>
                      {widget.type}
                    </div>
                    <div className='flex-1 font-condensed text-sm text-contrast'>
                      {widget.label}
                    </div>
                    <div className='flex items-center gap-03'>
                      <BtnSimple
                        sm
                        className={`${
                          !isHidden
                            ? 'bg-primary text-main'
                            : 'bg-main bg-opacity-30'
                        }`}
                        onClick={() =>
                          updateWidgetVisibility(widget.key, false)
                        }
                      >
                        Afficher
                      </BtnSimple>
                      <BtnSimple
                        sm
                        className={`${
                          isHidden
                            ? 'bg-secondary text-main'
                            : 'bg-main bg-opacity-30'
                        }`}
                        onClick={() => updateWidgetVisibility(widget.key, true)}
                      >
                        Masquer
                      </BtnSimple>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </AccordionDetails>
      </Accordion>
    </div>
  );
};
